/**
 * Tile sizing for the Quick Bytes bento grid — ROUTE_INVENTORY §2.12 #3 and #4.
 *
 * Extracted from `QuickBytesGrid` so the rule can be table-tested. The grid's
 * story asserts it is WIRED; the cases live in `bandPattern.test.ts`, because
 * what makes this correct is a property of every index rather than of the
 * twelve tiles a screenshot happens to show.
 *
 * ## Why a repeating band rather than random sizes
 *
 * The feed is infinite and paginated 60 at a time, and sizes are recomputed
 * from scratch on every render against the cumulative list. A random or
 * hash-based assignment would reshuffle the whole grid each time a page landed
 * — tiles the user was looking at would change size and move under the cursor.
 *
 * Keying size to `index % BAND_SIZE` makes the assignment a pure function of a
 * position that never changes once a tile has been placed. Page 2 arriving
 * cannot alter page 1, because none of page 1's indices moved. That is the
 * whole design, and it is the property the tests actually check.
 *
 * ## Why these positions
 *
 * Two large (2×2) and three tall (1×2) per 30, spaced so the verticals and the
 * square anchors interleave instead of clustering. Adjacent large tiles leave
 * holes that `grid-flow-row-dense` fills with unrelated tiles from further
 * down, which reads as the grid jumping.
 */

export type QuickBytesTileSize = 'regular' | 'tall' | 'large';

/** One repeating unit of the pattern. */
export const BAND_SIZE = 30;

/** Position within a band → size. Everything unlisted is `regular`. */
export const BAND_PATTERN: Record<number, QuickBytesTileSize> = {
  0: 'large',
  5: 'tall',
  14: 'tall',
  17: 'large',
  25: 'tall',
};

/** The size tile `index` gets from the band pattern alone, ignoring overrides. */
export function sizeForIndex(index: number): QuickBytesTileSize {
  return BAND_PATTERN[index % BAND_SIZE] ?? 'regular';
}

/**
 * Sizes for a whole loaded list, with the due-today override applied.
 *
 * §2.12 #4 — the FIRST `is_due_today` carousel is forced to `large` wherever it
 * lands, so today's headline reads as the headline even when its band position
 * would have made it an ordinary square. Only the first: promoting every due
 * item would fill the grid with 2×2 tiles on a day with a big backlog, which is
 * precisely when the user most needs to be able to scan it.
 *
 * The override never DEMOTES. A due item already sitting on a `large` position
 * stays large, and nothing else is touched — so the band's rhythm survives.
 */
export function resolveTileSizes(
  carousels: ReadonlyArray<{ is_due_today?: boolean }>
): QuickBytesTileSize[] {
  const sizes = carousels.map((_, index) => sizeForIndex(index));

  const firstDue = carousels.findIndex(c => c.is_due_today);
  if (firstDue >= 0 && sizes[firstDue] !== 'large') {
    sizes[firstDue] = 'large';
  }

  return sizes;
}
